import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, X, Grid, List, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import ProductCard from '@/components/ProductCard';
import { InFeedAd } from '@/components/AdBanner';
import { CATEGORIES, getCategoryById } from '@/data/categories';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category Tabs Component - Horizontal scrollable tabs
const CategoryTabs = ({ selectedCategory, onCategoryChange }) => (
  <div className="border-b bg-background sticky top-0 z-10">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <ScrollArea className="w-full whitespace-nowrap">
        <div className="flex gap-2 py-3">
          <button
            onClick={() => onCategoryChange('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex-shrink-0 ${
              selectedCategory === 'all' 
                ? 'bg-primary text-primary-foreground' 
                : 'bg-muted hover:bg-muted/80 text-muted-foreground'
            }`}
            data-testid="category-tab-all"
          >
            All Products
          </button>
          {CATEGORIES.map((category) => {
            const Icon = category.icon;
            const isActive = selectedCategory === category.id;
            return (
              <button
                key={category.id}
                onClick={() => onCategoryChange(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex-shrink-0 flex items-center gap-2 ${
                  isActive 
                    ? `${category.color} text-white` 
                    : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                }`}
                data-testid={`category-tab-${category.id.toLowerCase()}`}
              >
                <Icon className="w-4 h-4" />
                {category.name}
              </button>
            );
          })}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  </div>
);

// Loading Skeleton Component
const ProductSkeleton = () => (
  <div className="rounded-lg border p-4 space-y-4">
    <div className="aspect-square skeleton-pulse"></div>
    <div className="h-4 skeleton-pulse w-3/4"></div>
    <div className="h-3 skeleton-pulse w-1/2"></div>
  </div>
);

// Empty State Component
const EmptyState = ({ onClearFilters }) => (
  <div className="empty-state">
    <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
      <Filter className="w-8 h-8 text-muted-foreground" />
    </div>
    <h3 className="text-lg font-semibold mb-2">No products found</h3>
    <p className="text-muted-foreground mb-4">Try adjusting your filters</p>
    <Button variant="outline" onClick={onClearFilters} data-testid="no-products-clear-filters">
      Clear Filters
    </Button>
  </div>
);

// Filter Content Component (Sidebar)
const FilterContent = ({ filters, onFilterChange, hasActiveFilters, onClearFilters }) => (
  <div className="space-y-6">
    <div>
      <label className="text-sm font-medium mb-2 block">Search</label>
      <Input
        placeholder="Search products..."
        value={filters.search}
        onChange={(e) => onFilterChange('search', e.target.value)}
        data-testid="filter-search"
      />
    </div>
    
    <div>
      <label className="text-sm font-medium mb-3 block">Category</label>
      <div className="space-y-2">
        <button
          onClick={() => onFilterChange('category', 'all')}
          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
            filters.category === 'all' 
              ? 'bg-primary text-primary-foreground' 
              : 'hover:bg-muted'
          }`}
        >
          All Categories
        </button>
        {CATEGORIES.map((category) => {
          const Icon = category.icon;
          const isActive = filters.category === category.id;
          return (
            <button
              key={category.id}
              onClick={() => onFilterChange('category', category.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                isActive 
                  ? `${category.lightColor} ${category.textColor} font-medium` 
                  : 'hover:bg-muted'
              }`}
              data-testid={`filter-category-${category.id.toLowerCase()}`}
            >
              <Icon className="w-4 h-4" />
              {category.name}
            </button>
          );
        })}
      </div>
    </div>
    
    <div>
      <label className="text-sm font-medium mb-2 block">Product Type</label>
      <Select value={filters.type} onValueChange={(v) => onFilterChange('type', v)}>
        <SelectTrigger data-testid="filter-type">
          <SelectValue placeholder="All Types" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Types</SelectItem>
          <SelectItem value="physical">Physical Products</SelectItem>
          <SelectItem value="affiliate">Affiliate Deals</SelectItem>
        </SelectContent>
      </Select>
    </div>
    
    {hasActiveFilters && (
      <Button variant="outline" className="w-full" onClick={onClearFilters} data-testid="clear-filters">
        <X className="w-4 h-4 mr-2" />
        Clear Filters
      </Button>
    )}
  </div>
);

// Active Filters Display Component
const ActiveFilters = ({ filters, onFilterChange }) => (
  <div className="flex flex-wrap gap-2 mb-6">
    {filters.search && (
      <Badge variant="secondary" className="gap-1 pr-1">
        Search: {filters.search}
        <button onClick={() => onFilterChange('search', '')} className="ml-1 hover:bg-muted rounded p-0.5">
          <X className="w-3 h-3" />
        </button>
      </Badge>
    )}
    {filters.category && filters.category !== 'all' && (
      <Badge variant="secondary" className="gap-1 pr-1">
        Category: {filters.category}
        <button onClick={() => onFilterChange('category', 'all')} className="ml-1 hover:bg-muted rounded p-0.5">
          <X className="w-3 h-3" />
        </button>
      </Badge>
    )}
    {filters.type && filters.type !== 'all' && (
      <Badge variant="secondary" className="gap-1 pr-1">
        Type: {filters.type}
        <button onClick={() => onFilterChange('type', 'all')} className="ml-1 hover:bg-muted rounded p-0.5">
          <X className="w-3 h-3" />
        </button>
      </Badge>
    )}
  </div>
);

// Product Grid Component
const ProductGrid = ({ products, viewMode }) => (
  <div 
    className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`}
    data-testid="products-grid"
  >
    {products.map((product, index) => (
      <React.Fragment key={product.id}>
        <div style={{ animationDelay: `${index * 50}ms` }} className="animate-fade-in">
          <ProductCard product={product} />
        </div>
        {(index + 1) % 4 === 0 && index < products.length - 1 && (
          <div className={viewMode === 'grid' ? 'col-span-full' : ''}>
            <InFeedAd slot={`products-feed-${index}`} />
          </div>
        )}
      </React.Fragment>
    ))}
  </div>
);

// View Toggle Component
const ViewToggle = ({ viewMode, setViewMode }) => (
  <div className="hidden sm:flex border rounded-md">
    <Button
      variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
      size="icon"
      className="rounded-r-none"
      onClick={() => setViewMode('grid')}
      data-testid="view-grid"
    >
      <Grid className="w-4 h-4" />
    </Button>
    <Button
      variant={viewMode === 'list' ? 'secondary' : 'ghost'}
      size="icon"
      className="rounded-l-none"
      onClick={() => setViewMode('list')}
      data-testid="view-list"
    >
      <List className="w-4 h-4" />
    </Button>
  </div>
);

// Custom hook for fetching categories
const useCategories = () => {
  const [categories, setCategories] = useState([]);
  
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, []);
  
  return categories;
};

// Custom hook for fetching products
const useProducts = (filters) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.search) params.set('search', filters.search);
        if (filters.category && filters.category !== 'all') params.set('category', filters.category);
        if (filters.type && filters.type !== 'all') params.set('product_type', filters.type);
        
        const response = await axios.get(`${API}/products?${params.toString()}`);
        setProducts(response.data);
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [filters.search, filters.category, filters.type]);
  
  return { products, loading };
};

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('grid');
  const [filtersOpen, setFiltersOpen] = useState(false);
  
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || 'all',
    type: searchParams.get('product_type') || 'all'
  });

  const categories = useCategories();
  const { products, loading } = useProducts(filters);

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({ search: '', category: 'all', type: 'all' });
    setSearchParams({});
  }, [setSearchParams]);

  const hasActiveFilters = useMemo(() => 
    filters.search || (filters.category !== 'all') || (filters.type !== 'all'),
    [filters]
  );

  const gridClassName = useMemo(() => 
    `grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`,
    [viewMode]
  );

  return (
    <div className="min-h-screen">
      {/* Category Tabs - Horizontal scrollable */}
      <CategoryTabs 
        selectedCategory={filters.category} 
        onCategoryChange={(category) => handleFilterChange('category', category)} 
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Archivo, sans-serif' }}>
              {filters.category !== 'all' ? getCategoryById(filters.category).name : 'All Products'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {loading ? 'Loading...' : `${products.length} products found`}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
            
            {/* Mobile Filter Button */}
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="outline" className="gap-2" data-testid="mobile-filter-trigger">
                  <SlidersHorizontal className="w-4 h-4" />
                  Filters
                  {hasActiveFilters && <Badge variant="secondary" className="ml-1">Active</Badge>}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80">
                <SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader>
                <div className="mt-6">
                  <FilterContent 
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    hasActiveFilters={hasActiveFilters}
                    onClearFilters={clearFilters}
                  />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {hasActiveFilters && <ActiveFilters filters={filters} onFilterChange={handleFilterChange} />}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24 bg-card border rounded-lg p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Archivo, sans-serif' }}>
                <Filter className="w-4 h-4" />
                Filters
              </h3>
              <FilterContent 
                filters={filters}
                onFilterChange={handleFilterChange}
                hasActiveFilters={hasActiveFilters}
                onClearFilters={clearFilters}
              />
            </div>
          </aside>

          {/* Product Grid */}
          <div className="flex-1">
            {loading ? (
              <div className={gridClassName}>
                {[1, 2, 3, 4, 5, 6].map((i) => <ProductSkeleton key={`skeleton-${i}`} />)}
              </div>
            ) : products.length === 0 ? (
              <EmptyState onClearFilters={clearFilters} />
            ) : (
              <ProductGrid products={products} viewMode={viewMode} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
